import { Button, TooltipTrigger } from '@toggle/design-system';
import React, { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatInput } from '~/components/chat';
import { GradientBar } from '~/components/gradient-bar/GradientBar';
import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { useChatInputStore } from '~/stores/use-chat-input/useChatInput';
import { useChatNavigation } from '~/views/alfred/use-chat-navigation/useChatNavigation';

import * as S from './AlfredFooter.styles';

export interface AlfredFooterProps {
  renderChatInput?: (override?: { cta?: ReactNode }) => ReactNode;
  onReload?: () => void;
  selectedBadges?: ReactNode[];
  ctaLabel?: string;
  onCtaClick?: () => void;
  isCtaLoading?: boolean;
  suggestedQuestion?: string;
  progressDurationMs?: number;
  progressResetKey?: React.Key;
  onHoverChange: (hovered: boolean) => void;
  disableGradient?: boolean;
  isLoading?: boolean;
  submitCallback?: (message?: string) => void;
  isActive?: boolean;
}

export const AlfredFooter = ({
  onReload,
  suggestedQuestion,
  progressDurationMs = 10000,
  progressResetKey,
  onHoverChange,
  disableGradient = false,
  isLoading = false,
  submitCallback,
  isActive,
}: AlfredFooterProps) => {
  const { mode, setMode } = useChatNavigation();
  const { isSubmitButtonPinned, hasUserInteracted } = useChatInputStore(
    state => ({
      isSubmitButtonPinned: state.isSubmitButtonPinned,
      hasUserInteracted: state.hasUserInteracted,
    })
  );
  const { t } = useTranslation(['chat']);

  useEffect(() => {
    if (mode !== 'research') {
      setMode('research');
    }
  }, []);

  return (
    <S.AlfredFooterRoot
      role="contentinfo"
      $paused={hasUserInteracted || disableGradient}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
    >
      <S.Content>
        <S.LeftControls>
          <TooltipContainer
            label={t('chat:reload')}
            trigger={[TooltipTrigger.Hover]}
          >
            <Button
              variant="empty"
              size="small"
              iconName="ArrowRotateRight"
              aria-label={t('chat:reload')}
              onClick={!isLoading ? onReload : undefined}
              isLoading={isLoading && isActive}
            />
          </TooltipContainer>
        </S.LeftControls>

        <S.InputRegion $isPinned={isSubmitButtonPinned}>
          <ChatInput
            config={{
              autoFocus: false,
              alfredEverywhere: true,
              suggestedValue: suggestedQuestion,
            }}
            submitCallback={submitCallback}
          />
        </S.InputRegion>
      </S.Content>
      {!disableGradient && (
        <GradientBar
          key={progressResetKey}
          height="3px"
          variant="progress"
          durationMs={progressDurationMs}
        />
      )}
    </S.AlfredFooterRoot>
  );
};
